import { type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import React, { useCallback, useState } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import { IconButton } from 'components/uiKit/Button'
import DropDown from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { DeviceMode } from 'services/Store/Project/enums'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { addNode } from 'services/Store/Project/mutators/node'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'

import Picker from '../../Picker'
import * as s from './Absolute.module.scss'

interface IAbsoluteControlsProps {
  mode: IBlockMode
  block: Block
  node: NodeV2
}

const AbsoluteControls: React.FC<IAbsoluteControlsProps> = ({ block, mode, node }) => {
  const dispatch = useProjectDispatch()

  const [showPicker, setShowPicker] = useState(false)

  const onChange = useCallback(
    (mutator: (block: Block) => Block) =>
      dispatch(updateBlock({ id: block?.uuid, value: mutator(block) })),
    [block, dispatch],
  )

  const handleAddChild = useCallback(
    (newNode: NodeV2, data?: { nodes?: MapType<NodeV2>; elements?: MapType<EditorElement> }) => {
      onChange(addNode(node.id, newNode, data))
      setShowPicker(false)
    },
    [onChange, node.id],
  )

  if (mode.deviceMode !== DeviceMode.desktop) {
    return null
  }

  return (
    <AbsolutePortal
      name='add'
      placement='topLeft'
      translateX={`${ABSOLUTE_PORTAL_GAP}px`}
      translateY={`${ABSOLUTE_PORTAL_GAP}px`}
    >
      <div className={s.cell}>
        <DropDown
          onVisibleChange={setShowPicker}
          overlay={<Picker block={block} mode={mode} node={node} onAdd={handleAddChild} />}
          placement='right-start'
          styleType='clear'
          visible={showPicker}
        >
          <IconButton
            background='var(--color-absolute)'
            icon='add'
            name='addChild'
            size={KitSize.XS}
            round
          />
        </DropDown>
      </div>
    </AbsolutePortal>
  )
}

export default React.memo(AbsoluteControls)
